import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { organizationApi } from "../api/services";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const ROLE_LABELS = {
  super_admin:  "Super Admin",
  admin:        "Admin",
  tenant_admin: "Admin",
  operator:     "Operator",
  viewer:       "Viewer",
};

const NavBar = () => {
  const { user, logout, activeTenant } = useAuth();
  const navigate = useNavigate();
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);

  // Fetch org logo whenever the active tenant changes.
  // Uses the tenant-scoped getAll endpoint (accessible to any valid JWT role)
  // so it works regardless of whether the user's JWT claims are super_admin or admin.
  useEffect(() => {
    const tenantId = activeTenant?.id ?? (typeof activeTenant === "number" ? activeTenant : null);
    if (!tenantId) {
      setOrgLogoUrl(null);
      return;
    }

    organizationApi
      .getAll({ tenantId })
      .then((res) => {
        const rootOrgs = res.data?.data || [];
        const rootOrg  = rootOrgs.find((o) => o.logo_path);
        if (rootOrg) {
          setOrgLogoUrl(`${API_BASE}/api/organizations/${rootOrg.id}/logo`);
        } else {
          setOrgLogoUrl(null);
        }
      })
      .catch(() => setOrgLogoUrl(null));
  }, [activeTenant, user?.role]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const roleLabel = ROLE_LABELS[user?.role] || user?.role || "User";

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8 sticky top-0 z-10">
      {/* Left: platform title */}
      <div className="flex items-center gap-3">
        <img src="/hiketron.svg" alt="" className="h-5 opacity-60" onError={e => e.target.style.display='none'} />
        <h2 className="text-xl font-bold text-gray-800 tracking-tight">
          Industrial IoT Platform
        </h2>
      </div>

      <div className="flex items-center gap-6">
        {/* Notification Bell */}
        <button className="relative p-2 rounded-full hover:bg-gray-50 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-600 rounded-full border-2 border-white"></span>
        </button>

        {/* Role Badge */}
        <span className="text-xs font-medium px-2.5 py-1 bg-blue-50 text-blue-800 rounded-full border border-blue-200">
          {roleLabel}
        </span>

        {/* Org info */}
        <div className="flex items-center gap-2.5 py-1 px-2">
          {orgLogoUrl ? (
            <img
              src={orgLogoUrl}
              alt="org logo"
              className="w-9 h-9 rounded-lg object-contain border border-gray-200 bg-white p-0.5"
              onError={() => setOrgLogoUrl(null)}
            />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-blue-900 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-black text-sm">
                {(user?.org_name || 'Q').charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div className="hidden sm:block">
            <p className="text-sm font-semibold text-gray-800 leading-tight">
              {user?.org_name || 'Organization'}
            </p>
            <p className="text-xs text-gray-400">{user?.email}</p>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors px-2 py-1 rounded-lg hover:bg-red-50"
          title="Logout"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span className="hidden sm:inline">Logout</span>
        </button>
      </div>
    </header>
  );
};

export default NavBar;
